import React, {useState} from 'react';
import {
  checkFileMIMEType,
  checkFileSize,
  getFormattedSize,
} from '../../plasticos/helpers/files.helper';
import Swal from 'sweetalert2';
import {Button, Icon} from '../../components/Component';
import uploadFiles from '../../plasticos/helpers/add_file';
import {BACKEND} from '../../consts/backend';
import {updateTemporalesMroDocuments, insertTypeFileMro} from '../temporales';

const MroDocuments = ({poNumber, documentType = 'Import', idOrden = null, onUploadSuccess}) => {
  const MAXIMUM_FILE_SIZE = 20; // Size in MB
  const ALLOWED_FORMATS = '.pdf, .doc, .docx, .odt, image/*';

  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const getCurrentFiles = (event) => {
    const fileList = Object.values(event.target.files);
    const finalFileList = [];

    for (const file of fileList) {
      if (!checkFileMIMEType(file.type, ALLOWED_FORMATS)) {
        Swal.fire({
          icon: 'warning',
          title: 'Formato de archivo no soportado',
          html: `<small class="text-muted">El archivo ${file.name} no está soportado</small>`,
        });
        break;
      }
      if (!checkFileSize(file.size, MAXIMUM_FILE_SIZE)) {
        Swal.fire({
          icon: 'warning',
          title: `El tamaño máximo de archivo es ${MAXIMUM_FILE_SIZE}MB`,
          html: `<small class="text-muted">El archivo ${
            file.name
          } tiene un tamaño de ${getFormattedSize(file.size, 2)}</small>`,
        });
        break;
      }
      finalFileList.push(file);
    }

    if (finalFileList.length > 0) {
      setFiles([...files, ...finalFileList]);
    }
    event.target.value = event.target.defaultValue; // Reset input cached value
  };

  const handleFilesSubmit = async (event) => {
    event.preventDefault();
    if (event.isTrusted && poNumber !== '') {
      const proceed = await Swal.fire({
        title: 'Estos archivos serán almacenados en el servidor. ¿Continuar?',
        text: `El directorio de destino es /mro-temporales/${poNumber} (${documentType})`,
        icon: 'question',
        confirmButtonText: 'Sí',
        cancelButtonText: 'No. Cancelar',
        showCancelButton: true,
      });
      if (!proceed?.isConfirmed) {
        return;
      }
      setSubmitting(true);
      const uploadedFiles = [];
      let date = new Date();
      
      try {
        for (const file of files) {
          // Usar el helper de upload existente para S3
          const uploadDataResult = await uploadFiles(file, `mro-temporales/${poNumber}`, date);
          
          // Usar el mismo endpoint que detailorder para registrar el archivo
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await fetch(`${BACKEND}/order/${poNumber}/file`, {
            method: 'POST',
            body: formData,
          });
          
          if (response.ok) {
            uploadedFiles.push(true);
            // Asignar el tipo directamente (Import o Export)
            try {
              const fileNormalizedName = file.name.replace(/  +/g, ' ').replaceAll(' ', '_');
              await insertTypeFileMro({
                id_orden: idOrden,
                id_file: uploadDataResult?.id || null,
                type: documentType,
                po_number: poNumber,
                file_name: fileNormalizedName,
              });
            } catch (errType) {
              console.error('Error asignando tipo de documento:', errType);
            }
          } else {
            uploadedFiles.push(false);
          }
        }
        
        if (uploadedFiles.filter(Boolean).length === files.length) {
          // Actualizar el campo documents a 1 en la base de datos
          try {
            await updateTemporalesMroDocuments(poNumber, 1);
          } catch (error) {
            console.error('Error updating documents field:', error);
          }
          
          await Swal.fire({
            title: `${
              files.length > 1 ? 'Archivos' : 'Archivo'
            } subido(s) exitosamente como ${documentType}`,
            icon: 'success',
          });
          setFiles([]);
          // Notificar al componente padre que se subieron archivos
          if (onUploadSuccess) {
            onUploadSuccess();
          }
        } else {
          await Swal.fire({
            title: `${
              uploadedFiles.filter(Boolean).length > 1
                ? 'Algunos archivos no fueron'
                : 'Un archivo no fue'
            } subido(s)`,
            icon: 'warning',
          });
          setFiles([
            ...files.filter((_, index) => uploadedFiles[index]),
          ]);
        }
      } catch (reason) {
        console.error('Error uploading files:', reason);
        Swal.fire({
          title: 'Error inesperado',
          text: 'Por favor intenta de nuevo más tarde',
          icon: 'error',
        });
      }
      setSubmitting(false);
      event.target.reset();
    } else {
      Swal.fire({
        title: '¡Oops!',
        text: 'Por favor ingresa un N° de PO válido',
        icon: 'warning',
      });
    }
  };

  const removeFile = (event, index) => {
    if (event.isTrusted) {
      const currentFiles = Object.create(files);
      currentFiles.splice(index, 1);
      setFiles([...currentFiles]);
    }
  };

  return (
    <div style={{marginTop: '10px'}}>
      <form onSubmit={handleFilesSubmit}>
        <div className="input-group">
          <div className="custom-file">
            <input
              type="file"
              className="custom-file-input"
              onChange={getCurrentFiles}
              multiple
              id={`file-uploader-${poNumber}-${documentType}`}
              accept={ALLOWED_FORMATS}
            />
            <label
              className="custom-file-label"
              htmlFor={`file-uploader-${poNumber}-${documentType}`}
            >
              Seleccionar archivos ({documentType})
            </label>
          </div>
        </div>
        
        {files.length > 0 && (
          <div style={{marginTop: '10px'}}>
            <h6>Archivos seleccionados:</h6>
            {files.map((file, index) => (
              <div 
                key={index} 
                style={{
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '5px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginBottom: '5px'
                }}
              >
                <span>
                  <Icon name="file" style={{marginRight: '5px'}} />
                  {file.name} ({getFormattedSize(file.size, 2)})
                </span>
                <Button
                  color="danger"
                  size="sm"
                  onClick={(e) => removeFile(e, index)}
                >
                  <Icon name="trash" />
                </Button>
              </div>
            ))}
            
            <Button
              color="primary"
              type="submit"
              disabled={submitting}
              style={{marginTop: '10px'}}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm" style={{marginRight: '5px'}} />
                  Subiendo...
                </>
              ) : (
                <>
                  <Icon name="upload" style={{marginRight: '5px'}} />
                  Subir archivos
                </>
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default MroDocuments;
